import React, { useMemo, useState } from 'react';
import {
  FormControlLabel,
  Checkbox,
  Divider,
  IconButton,
  Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Tooltip from '@src/components/Tooltip';
import BadgeAvatar from '@src/components/BadgeAvatarNew';
import LoadingCircular from '@src/new-components/LoadingCircular';
import Typography from '@src/new-components/Typography';
import { convertFormatDate } from '@src/utils/date';
import RunningWithErrorsRoundedIcon from '@mui/icons-material/RunningWithErrorsRounded';
import SlowMotionVideoRoundedIcon from '@mui/icons-material/SlowMotionVideoRounded';
import { COLOR } from '@src/styles/color';
import SpeedNewV2 from '@src/new-components/Speed';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { useSelector, useDispatch } from 'react-redux';
import CommonButton from '@src/new-components/Button';
import actions from '@src/redux/actions';
import {
  audioExpired,
  getSpeedFromBlockSelected,
  getEmphasisFromBlockSelected,
  getVoiceFromBlockSelected,
  handleDownloadParagraphsZip,
  sanitizeTitleFile,
  getExportingState,
} from '@src/services/project';
import EmphasisPopover from '@src/containers/EmphasisPopover';
import VoiceSelectionDialog from '@src/containers/VoiceSelectionDialog';
import { BLOCK_STATUS, PROJECT_PRODUCT } from '@src/constants/project';
import moment from 'moment';
import { SUB_STATUS } from '@src/constants/voiceCloning';
import LimitedFeature from '@src/components/LimitedFeature';
import { countTextLength } from '@src/services/tts';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import useExportHistoryEnabled from '@src/hooks/useExportHistoryEnabled';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { StyledConfigMultiBlock } from './index.style';

const ConfigMultiBlock = ({
  handleConvertBlockSelected,
  filterBlockResults,
}) => {
  const { getFeatureValue } = useFeatureFlags();
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const selectedBlockIds = useSelector(
    (state) => state.project.selectedBlockIds,
  );
  const { user } = useSelector((state) => state.auth);

  const enableCreditUsagePreview = getFeatureValue(
    FEATURE_KEYS.ENABLE_CREDIT_USAGE_PREVIEW,
    { userId: user.id, email: user.email },
  );

  const {
    title,
    audioType,
    voiceDefault,
    currentFilteringStatus,
    currentPlayingBlockId,
    isPlaying,
    currentFilteringVoiceCode,
    isGeneratingNarration,
    exports,
    isDownloading,
  } = useSelector((state) => state.project);
  const multiBlock = useSelector((state) => state.project.multiBlock);

  const exportHistoryEnabled = useExportHistoryEnabled();

  const exportBannerEnabled = getFeatureValue(
    FEATURE_KEYS.PRESENTATION_EXPORT_VIDEO,
    { userId: user.id, email: user.email, phoneNumber: user.phoneNumber },
  );
  const { isExporting } = getExportingState({
    exports,
    isDownloading,
    product: PROJECT_PRODUCT.PRESENTATION,
  });
  // Exporting no longer locks editing (see Presentation/Content), behind the
  // export-history flag.
  const isExportLocked =
    exportBannerEnabled && isExporting && !exportHistoryEnabled;
  const { blockIds, blocks } = multiBlock || {};
  const dispatch = useDispatch();
  const voice = getVoiceFromBlockSelected({
    selectedBlockIds,
    blocks,
    voiceDefault,
  });
  const speed = getSpeedFromBlockSelected(selectedBlockIds, blocks);
  const emphasis = getEmphasisFromBlockSelected(selectedBlockIds, blocks);
  const [openVoices, setOpenVoices] = useState(false);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(null);

  const blocksSelected = useMemo(
    () => selectedBlockIds.map((blockId) => blocks[blockId]),
    [blockIds, blocks, selectedBlockIds],
  );
  const isSynthesizing = blocksSelected.some(
    (block) => block?.blockStatus === BLOCK_STATUS.IN_PROGRESS,
  );
  const blockSynthesizing = blocksSelected.filter(
    (block) => block?.blockStatus === BLOCK_STATUS.IN_PROGRESS,
  );

  const isInValidPublicVoiceCloning =
    voice?.userId !== user?.id &&
    voice?.discardAt &&
    moment().isAfter(voice?.discardAt);

  const isPublicVoiceCloning = voice?.status === SUB_STATUS.PUBLIC;
  const isVoiceCloningAboutToRemoved =
    isPublicVoiceCloning && voice?.discardAt
      ? moment().isBefore(voice?.discardAt)
      : false;
  const allBlocksHaveEmphasis = blocksSelected?.every(
    (block) => block?.hasEmphasis,
  );
  const blockTextEmpty = blocksSelected?.filter(
    (block) => !block?.text?.length,
  );
  const blockHasText = blocksSelected?.filter((block) => block?.text?.length);
  const blockAvailable = blocksSelected?.filter((block) =>
    [
      BLOCK_STATUS.DRAFT,
      BLOCK_STATUS.AUDIO_EXPIRED,
      BLOCK_STATUS.ERROR,
    ].includes(block?.blockStatus),
  );
  const disableConvertBtn =
    blockTextEmpty?.length > 0 &&
    blockAvailable?.length === blockTextEmpty?.length;
  const canConvertSelectedBlocks =
    blocksSelected?.length > 0 &&
    (blockAvailable.length ||
      blockSynthesizing?.length !== blocksSelected?.length) &&
    !disableConvertBtn;
  const isEnableConvertBtn = canConvertSelectedBlocks && !isGeneratingNarration;

  const canDownloadSelectedBlocks =
    blocksSelected?.length > 0 &&
    blockHasText?.length > 0 &&
    blockHasText?.every(
      (block) => block?.blockStatus === BLOCK_STATUS.AUDIO_AVAILABLE,
    );
  const isEnableDownloadBtn =
    canDownloadSelectedBlocks && !isGeneratingNarration;

  const isDisableCheckbox =
    Object.keys(filterBlockResults)?.length === 0 || isExportLocked;

  const handleChangeSelectAll = (e) => {
    const isChecked = e.target.checked;

    // If unchecked or already have selections, unselect all
    if (!isChecked || selectedBlockIds.length > 0) {
      dispatch(actions.project.unselectAllBlocks());
      return;
    }
    const blocksFilter = Object.keys(filterBlockResults);
    // If nothing is selected and the event is "select all"
    const filteredBlockIds = blocksFilter.filter((blockId) => {
      const block = blocks[blockId];
      // When filtering ALL, include blocks matching currentFilteringVoiceCode (or all if code empty)
      if (
        currentFilteringStatus === BLOCK_STATUS.ALL ||
        currentFilteringVoiceCode === ''
      ) {
        const blockVoiceCode = block.voice?.code || voiceDefault?.code;
        return (
          currentFilteringVoiceCode === '' ||
          blockVoiceCode === currentFilteringVoiceCode
        );
      }
      // Otherwise, only blocks with current filtering status

      return (
        block.blockStatus === currentFilteringStatus &&
        (block.voice?.code || voiceDefault?.code) === currentFilteringVoiceCode
      );
    });

    dispatch(actions.project.selectBlocks(filteredBlockIds));
  };
  const isChecked =
    selectedBlockIds.length === blockIds.length &&
    selectedBlockIds.every((item) => blockIds.includes(item));

  const handleOpenVoices = () => {
    setOpenVoices(true);
  };

  const handleCloseVoices = () => {
    setOpenVoices(false);
  };

  const handleChangeVoice = (newVoice) => {
    const isVoiceHasEmphasis = newVoice?.hasEmphasis;
    const blockUpdates = selectedBlockIds.map((blockId) => ({
      blockId,
      block: {
        voice: newVoice,
        voiceCode: newVoice?.code,
        hasEmphasis: isVoiceHasEmphasis,
        emphasis: isVoiceHasEmphasis ? blocks[blockId]?.emphasis : 0,
      },
    }));
    dispatch(actions.project.updateMultipleBlocks(blockUpdates));
  };

  const handleChangeSpeed = (name, value) => {
    const blockUpdates = selectedBlockIds.map((blockId) => ({
      blockId,
      block: { speed: value },
    }));
    dispatch(actions.project.updateMultipleBlocks(blockUpdates));
  };

  const handleChangeEmphasis = (name, value) => {
    const blockUpdates = selectedBlockIds.map((blockId) => ({
      blockId,
      block: { emphasis: value },
    }));
    dispatch(actions.project.updateMultipleBlocks(blockUpdates));
  };

  const handleConvertBlockToAudio = () => {
    if (!isEnableConvertBtn) return;
    if (isPlaying) {
      dispatch(actions.project.setIsPlaying(false));
      dispatch(actions.project.setIsMultiBlockMode(false));
      dispatch(actions.project.setCurrentBlockId(null));
    } else if (
      currentPlayingBlockId &&
      selectedBlockIds.includes(currentPlayingBlockId)
    ) {
      dispatch(actions.project.setIsPlayingBlock(false));
    }
    handleConvertBlockSelected(
      blocksSelected.filter((block) => block?.text?.length),
    );
  };

  const showErrorNotification = (message, params = {}) =>
    dispatch(
      actions.noti.push({
        severity: 'error',
        message: t(message, params),
      }),
    );

  const handleDownloadMultiBlock = async () => {
    if (!isEnableDownloadBtn || loadingDownload) return;
    setLoadingDownload(true);
    const audioLinks = blocksSelected
      .filter((block) => block?.audioLink)
      .map((block) => {
        const position = blockIds?.findIndex((id) => id === block.id);

        return {
          audioLink: block.audioLink,
          title: block?.text?.substring(0, 30),
          requestId: block.requestId,
          position,
          newTitle: block?.title || sanitizeTitleFile(block?.text || ''),
        };
      });

    // Check has voice audio expired
    const hasVoiceAudioExpired = audioLinks.some((block) =>
      audioExpired(block?.audioExpiredAt),
    );
    if (hasVoiceAudioExpired) {
      showErrorNotification('someThingWrong');
      setLoadingDownload(false);
      return;
    }

    const onProgress = (progress) => {
      setDownloadProgress(progress);
    };
    await handleDownloadParagraphsZip(audioLinks, title, audioType, onProgress);
    setLoadingDownload(false);
    setDownloadProgress(null);
  };

  // Calculate costs credit for all blocks without audio link
  const blockWithoutAudioLink = blockHasText.filter(
    (block) => block?.blockStatus !== BLOCK_STATUS.IN_PROGRESS,
  );

  const costsCredit = useMemo(
    () =>
      blockWithoutAudioLink.reduce(
        (acc, block) =>
          acc +
          (block?.voice?.creditFactor || voiceDefault?.creditFactor) *
            countTextLength(block?.text || ''),
        0,
      ),
    [blockWithoutAudioLink, voiceDefault],
  );

  return (
    <>
      <StyledConfigMultiBlock>
        <FormControlLabel
          className="checkbox-container"
          style={{ paddingLeft: '8px' }}
          control={
            <Checkbox
              className={`checkbox-color ${
                isDisableCheckbox ? 'disabled-btn' : ''
              }`}
              indeterminate={
                selectedBlockIds.length > 0 &&
                selectedBlockIds.length < blockIds.length
              }
              disabled={isDisableCheckbox}
            />
          }
          labelPlacement="end"
          disabled={isDisableCheckbox}
          label={
            selectedBlockIds.length > 0
              ? t('deselectAllV2', {
                  currentSelected: selectedBlockIds.length,
                  totalSelected: Object.keys(filterBlockResults)?.length,
                })
              : t('selectAll')
          }
          onChange={handleChangeSelectAll}
          checked={isChecked}
          classes={{ label: 'checkbox-label' }}
        />
        {selectedBlockIds.length > 0 && (
          <>
            <Divider orientation="vertical" flexItem />
            <div className="config-multi-block-voice">
              <Tooltip
                arrow
                title={isSynthesizing ? '' : t('chooseVoice')}
                placement="top"
              >
                <CommonButton
                  variant="transparent"
                  onClick={handleOpenVoices}
                  data-id="open-voice-list"
                  customStyle={{
                    gap: '8px',
                    padding: '4px 6px',
                    borderRadius: '16px',
                    '&:focus': {
                      backgroundColor: 'transparent',
                    },
                    '&:hover': {
                      background: COLOR.secondary[80],
                    },
                    '&:disabled': {
                      opacity: 0.5,
                      cursor: 'not-allowed',
                      pointerEvents: 'auto',
                      '&:hover': {
                        backgroundColor: COLOR.white,
                        boxShadow: 'none',
                      },
                    },
                  }}
                  disabled={isSynthesizing}
                >
                  <BadgeAvatar
                    width={25}
                    smallImgWidth={12}
                    img={voice?.roundImage}
                    smallImg={voice?.language?.roundImage}
                    type="image"
                    newUiAvatar
                  />
                  <Typography
                    variant="body-14-medium"
                    color={
                      isInValidPublicVoiceCloning
                        ? COLOR.redPrimary
                        : COLOR.indigo.default
                    }
                  >
                    {voice?.name || t('chooseVoice')}
                  </Typography>
                </CommonButton>
              </Tooltip>
              <LimitedFeature voiceFeatures={voice?.features} />
              {isVoiceCloningAboutToRemoved && (
                <Tooltip
                  arrow
                  title={t('voiceCloningDiscardAt', {
                    date: convertFormatDate(voice?.discardAt),
                  })}
                  placement="top"
                >
                  <RunningWithErrorsRoundedIcon className="returning-error-icon" />
                </Tooltip>
              )}
            </div>
            <SpeedNewV2
              currentSpeed={speed}
              disabled={isSynthesizing}
              onChange={handleChangeSpeed}
            />
            {allBlocksHaveEmphasis && (
              <EmphasisPopover
                currentEmphasis={emphasis}
                onChangeEmphasis={handleChangeEmphasis}
                isDisabled={isSynthesizing}
              />
            )}
            <Divider orientation="vertical" flexItem />
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              sx={{ marginLeft: 'auto' }}
            >
              <>
                <Tooltip
                  sx={{ display: canConvertSelectedBlocks ? 'block' : 'none' }}
                  arrow
                  title={
                    enableCreditUsagePreview ? (
                      <div
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                          __html: t('costsCredit', {
                            value: costsCredit?.toLocaleString(language),
                          }),
                        }}
                      />
                    ) : (
                      t('convertToAudioToListen')
                    )
                  }
                  placement="top"
                >
                  <div className={isEnableConvertBtn ? '' : 'disabled-btn'}>
                    <IconButton
                      onClick={handleConvertBlockToAudio}
                      className="editor-toolbar-btn"
                      disabled={!isEnableConvertBtn}
                    >
                      <SlowMotionVideoRoundedIcon className="editor-toolbar-btn-icon" />
                    </IconButton>
                  </div>
                </Tooltip>
                <Tooltip
                  sx={{
                    display:
                      canDownloadSelectedBlocks && !loadingDownload
                        ? 'block'
                        : 'none',
                  }}
                  arrow
                  title={t('download')}
                  placement="top"
                >
                  <div className={isEnableDownloadBtn ? '' : 'disabled-btn'}>
                    {loadingDownload ? (
                      <IconButton className="editor-toolbar-btn">
                        <LoadingCircular
                          value={downloadProgress?.percentage}
                          variant="base"
                          showPercentage
                        />
                      </IconButton>
                    ) : (
                      <IconButton
                        onClick={handleDownloadMultiBlock}
                        className="editor-toolbar-btn"
                        disabled={!isEnableDownloadBtn || loadingDownload}
                      >
                        <DownloadRoundedIcon className="editor-toolbar-btn-icon" />
                      </IconButton>
                    )}
                  </div>
                </Tooltip>
              </>
            </Box>
          </>
        )}
      </StyledConfigMultiBlock>
      <VoiceSelectionDialog
        open={openVoices}
        activeVoiceId={voice && voice.id}
        activeVoiceCode={voice && voice.code}
        onClose={handleCloseVoices}
        onChangeVoice={handleChangeVoice}
        hasLanguageFilter
      />
    </>
  );
};

export default ConfigMultiBlock;
